import { IonButton, IonButtons, IonCard, IonCardContent, IonCardHeader, IonCardTitle, IonCol, IonContent, IonGrid, IonHeader, IonIcon, IonImg, IonInput, IonItem, IonLabel, IonMenuButton, IonNote, IonPage, IonRow, IonSelect, IonSelectOption, IonTitle, IonToolbar, useIonRouter, useIonViewDidEnter } from '@ionic/react';
import { addCircleOutline, carOutline, chevronBackOutline } from 'ionicons/icons';
import React, { useState } from 'react';
import { useAppContext } from '../AppProvider';
import Alert from '@mui/material/Alert';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { AlertTitle } from '@mui/material';

const VehicleCreate: React.FC = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [yearOptions, setYearOptions] = useState<any>([]);
    
    const router = useIonRouter();

    const { authKey, authenticateMember, limits, profiles, settings, states, getStates, SWFullHorizLogo, SWTextDarkLogo, textLogoLoaded, fadeInTextLogo, showToast } = useAppContext();
    
    //
    // Authenticate Member
    //
    useIonViewDidEnter(() => { 
        const authenticate = async () => {
            const authenticated = await authenticateMember();         
            if(!authenticated.mt) router.push('/login', 'forward');
            else {
                await getYears();
                await getStates(); 
                setLoading(false);
            }
        };
        authenticate();
    }); 
    

    const insertVehicle = async (e: any) => {
        e.preventDefault();
        const form = e.target; 
        const formName = form.name;
        const btn: any = document.getElementById(formName + 'Submit');
        btn.innerHTML = 'Saving...';
        btn.setAttribute('disabled', '');
        const formData = new FormData(form);
        formData.append('mt', authKey.mt);
        
        try {
            const response = await fetch('https://api.besolowise.com/vehicles/insert/', {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            if(response.ok) {
                const obj = await response.json(); 
                
                if(obj.success) router.push('/account/vehicle/edit/' + obj.vt, 'back');
                else {
                    showToast({
                        message: 'Error: ' + obj.message,
                        duration: 5000,
                        color: 'danger',
                    });
                }
                btn.innerHTML = 'Save';
                btn.removeAttribute('disabled');                    
            } else {
                btn.innerHTML = 'Save';
                btn.removeAttribute('disabled');
                showToast({
                    message: 'Response Error',
                    duration: 2000,
                    color: 'danger',
                });
            } 
        } catch(error) {
            showToast({
                message: 'Connection Error: Couldn\'t create this vehicle. ' + error + '. Try again.',
                duration: 3000,
                color: 'danger',
                position: 'middle'
            });
        }   
    };

    const getYears = async () => {
        // Fill 'vehicleYear' options
        const today = new Date();
        const thisYear = today.getFullYear();
        const start = thisYear + 1;
        const end = thisYear - 60;
        const yearsOptions = [];
        for(let y = start; y >= end; y--) yearsOptions.push(y.toString());
        setYearOptions(yearsOptions); 
    };

    return (
        <IonPage>
            <IonHeader>
                <IonToolbar>
                    <IonButtons slot='start'>
                        <IonButton color={'tertiary'} routerDirection='back' routerLink='/account/vehicles'>Vehicles</IonButton>
                    </IonButtons>
                    <IonTitle><IonImg className={textLogoLoaded ? 'fade-in' : 'fade-out'} src={settings.theme == '1' ? SWTextDarkLogo : SWFullHorizLogo} onIonImgDidLoad={fadeInTextLogo}></IonImg></IonTitle>
                    <IonButtons slot='end'>
                        <IonMenuButton color={'tertiary'} />
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding account-content-bg" id="VehicleContent">
                <IonGrid>
                    <IonRow>
                        <IonCol size='12'>
                            <h1><IonIcon color={'secondary'} icon={carOutline}></IonIcon> New Vehicle<br /><span>Save details about your vehicles</span></h1>
                        </IonCol>
                    </IonRow>
                </IonGrid>

                {!loading && profiles?.vehicles >= limits?.vehicles 
                    && <IonCard>
                        <IonCardHeader>
                            <IonCardTitle className='ion-margin-bottom ion-text-center' color={'tertiary'}>
                                You've reached the limit of Vehicles for your current plan.
                            </IonCardTitle>
                        </IonCardHeader>
                        <IonCardContent>
                            <p className='ion-margin-bottom ion-text-center'>
                                You can add more Vehicles by upgrading to a SoloWise Premium plan. 
                            </p>
                            <p className='ion-margin-bottom ion-text-center'>                            
                                <IonButton className='ion-margin' color={'secondary'} routerLink='/account/plans' routerDirection='forward'><IonIcon icon={addCircleOutline} slot='start'></IonIcon> Upgrade Now</IonButton>
                            </p>
                        </IonCardContent>
                    </IonCard>
                }

                {!loading && profiles?.vehicles < limits?.vehicles 
                    && <IonCard>                    
                        <IonCardContent>    
                            <form name='vehicleForm' onSubmit={insertVehicle}>
                                <IonGrid>                          
                                    <IonRow>
                                        <IonCol size='6'>   
                                            <IonLabel color={'dark'}><b>Year</b></IonLabel>                 
                                            <IonSelect fill='outline' labelPlacement='stacked' name='vehicleYear' placeholder='Year'>                                     
                                                {yearOptions.map((year:string, index:number) => (
                                                    <IonSelectOption key={index} value={year}>{year}</IonSelectOption>
                                                ))}
                                            </IonSelect>
                                        </IonCol>
                                        <IonCol size='6'>
                                            <IonLabel color={'dark'}><b>Manufacturer</b></IonLabel>
                                            <IonInput fill='outline' name='vehicleMfg' placeholder='Manufacturer' type='text'></IonInput>                                                
                                        </IonCol>
                                    </IonRow>

                                    <IonRow>
                                        <IonCol size='12'>   
                                            <IonLabel color={'dark'}><b>Model</b></IonLabel>                 
                                            <IonInput fill='outline' name='vehicleModel' placeholder='Model' type='text'></IonInput>
                                        </IonCol>
                                    </IonRow>

                                    <IonRow>
                                        <IonCol size='12'>   
                                            <IonLabel color={'dark'}><b>Color & Trim</b></IonLabel>                 
                                            <IonInput fill='outline' name='vehicleColor' placeholder='Color' type='text'></IonInput>
                                        </IonCol>
                                    </IonRow>

                                    <IonRow>
                                        <IonCol size='6'>
                                            <IonLabel color={'dark'}><b>State</b></IonLabel>
                                            <IonSelect fill='outline' name='vehicleLicenseState' labelPlacement='stacked' placeholder='State'>
                                                <IonSelectOption value=''>None</IonSelectOption>
                                                {states.map((state:string, index:number) => (
                                                    <IonSelectOption key={index} value={state[0]}>{state[1]}</IonSelectOption>
                                                ))}
                                            </IonSelect>                                             
                                        </IonCol>
                                        <IonCol size='6'>   
                                            <IonLabel color={'dark'}><b>License Plate #</b></IonLabel>                 
                                            <IonInput fill='outline' name='vehicleLicense' placeholder='License' type='text'></IonInput>
                                        </IonCol>                                    
                                    </IonRow>                                     
                                </IonGrid>

                                <IonButton className='ion-margin-top' color={'secondary'} expand="block" id='vehicleFormSubmit' type='submit'>Save</IonButton>
                            </form>                        
                        </IonCardContent>
                    </IonCard>
                }
            </IonContent>
        </IonPage>
    );
};

export default VehicleCreate;
